import React from "react";
import { FaGithub, FaExternalLinkSquareAlt, FaChevronDown, FaChevronUp } from "react-icons/fa";

const ProjectCard = ({ project, isOpen, setOpenProject }) => {
  const isExpanded = isOpen === project.name;

  return (
    <div
      className={`relative w-full cursor-pointer border-t border-zinc-200 py-4 text-left text-base transition-colors duration-150 dark:border-zinc-800 ${
        isExpanded
          ? "bg-zinc-50/70 dark:bg-zinc-900/30"
          : "hover:bg-zinc-50/70 dark:hover:bg-zinc-900/30"
      }`}
      onClick={() => setOpenProject(isExpanded ? null : project.name)}
    >

      <div className="flex w-full items-start justify-between px-1">
        {/* Project Image */}
        <div className="mr-4 shrink-0">
          <img
            src={project.image || `${process.env.PUBLIC_URL}/placeholder.png`}
            alt={`${project.name} by Milad Mehri`}
            className="h-11 w-11 rounded-md border border-zinc-200 bg-white object-cover dark:border-zinc-800"
          />
        </div>

        {/* Project Info */}
        <div className="flex min-w-0 flex-grow flex-col">
          <div className="flex w-full items-start justify-between gap-3">
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center flex-wrap gap-2">
                {project.link ? (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-words text-base font-semibold text-zinc-950 hover:underline dark:text-white"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {project.name}
                  </a>
                ) : (
                  <b className="break-words text-base text-zinc-950 dark:text-white">{project.name}</b>
                )}
                {project.badges?.map((badge, idx) => (
                  <span 
                    key={idx}
                    className="rounded border border-zinc-200 px-1.5 py-0.5 text-xs font-medium text-zinc-500 dark:border-zinc-700 dark:text-zinc-400"
                  >
                    {badge.text}
                  </span>
                ))}
              </div>
              <p className="mt-1 break-words text-sm text-zinc-500 dark:text-zinc-400">
                {project.tech}
                {project.date ? (
                  <span className="text-zinc-400 dark:text-zinc-500"> | {project.date}</span>
                ) : null}
              </p>
            </div>

            {/* Icons */}
            <div className="flex shrink-0 items-center">
              {project.github && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={project.github}
                  className="p-1 text-zinc-500 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white"
                  onClick={(e) => e.stopPropagation()}
                  title="View on GitHub"
                  aria-label={`Open ${project.name} on GitHub`}
                >
                  <FaGithub size={18} />
                </a>
              )}
              {project.link && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={project.link}
                  className="p-1 text-zinc-500 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white"
                  onClick={(e) => e.stopPropagation()}
                  title="View Live Project"
                  aria-label={`Open ${project.name}`}
                >
                  <FaExternalLinkSquareAlt size={18} />
                </a>
              )}
              <button
                className="p-1 text-zinc-500 transition-colors duration-150 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenProject(isExpanded ? null : project.name);
                }}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${project.name}`}
              >
                {isExpanded ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}
              </button>
            </div>
          </div>

          {/* Expandable Section with Smooth Transition */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? "mt-3 max-h-[520px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <ul className="list-disc space-y-1.5 pl-5 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
              {project.bulletPoints.map((point, idx) => (
                <li className="self-start" key={idx}>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
